import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Card,
  Alert,
  Snackbar,
  Paper,
  Skeleton,
  Slider
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Add,
  Business,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

interface Franchise {
  id: string;
  name: string;
  logoURL: string;
  createdAt?: any;
}

const FranchisesPage: React.FC = () => {
  const navigate = useNavigate();
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(null);
  const [name, setName] = useState('');
  const [logoPreview, setLogoPreview] = useState<string>('');
  
  // Crop States
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchFranchises = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/franchises', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setFranchises(response.data.data);
      }
    } catch (error: any) {
      console.error('Franchise listesi yüklenirken hata:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Zincir markalar yüklenirken hata oluştu',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFranchises();
  }, []);

  const handleOpenAddDialog = () => {
    setSelectedFranchise(null);
    setName('');
    setLogoPreview('');
    setOwnerName('');
    setOwnerEmail('');
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (franchise: Franchise) => {
    setSelectedFranchise(franchise);
    setName(franchise.name);
    setLogoPreview(franchise.logoURL);
    setDialogOpen(true);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: "Logo boyutu 5MB'dan küçük olmalıdır", severity: 'error' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Lütfen geçerli bir resim dosyası seçin', severity: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setCropDialogOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    if (!croppedAreaPixels || !imageToCrop) return;

    const image = new Image();
    image.src = imageToCrop;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = 300;
    canvas.height = 300;

    ctx?.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      300,
      300
    );

    const croppedBase64 = canvas.toDataURL('image/png', 0.9);
    setLogoPreview(croppedBase64);
    setCropDialogOpen(false);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setSnackbar({ open: true, message: 'Franchise adı zorunludur', severity: 'error' });
      return;
    }

    if (saving) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const payload: any = { name: name.trim() };
      
      if (logoPreview.startsWith('data:image/')) {
        payload.logoBase64 = logoPreview;
      } else {
        payload.logoURL = logoPreview;
      }

      if (!selectedFranchise && ownerName.trim() && ownerEmail.trim()) {
        payload.ownerName = ownerName.trim();
        payload.ownerEmail = ownerEmail.trim();
      }

      if (selectedFranchise) {
        // Edit
        const response = await axios.put(`https://api.sobcof.com/api/franchises/${selectedFranchise.id}`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success) {
          setSnackbar({ open: true, message: 'Zincir marka başarıyla güncellendi', severity: 'success' });
        }
      } else {
        // Create
        const response = await axios.post('https://api.sobcof.com/api/franchises', payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success) {
          let msg = 'Yeni zincir marka başarıyla oluşturuldu';
          if (response.data.ownerAccount?.temporaryPassword) {
            msg += `. Ana yönetici şifresi: ${response.data.ownerAccount.temporaryPassword}`;
          }
          setSnackbar({ open: true, message: msg, severity: 'success' });
        }
      }
      setDialogOpen(false);
      fetchFranchises();
    } catch (error: any) {
      console.error('Kaydedilirken hata:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'İşlem sırasında hata oluştu',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu zincir markayı silmek istediğinize emin misiniz?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`https://api.sobcof.com/api/franchises/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Zincir marka silindi', severity: 'success' });
        fetchFranchises();
      }
    } catch (error: any) {
      console.error('Silinirken hata:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Silme işlemi başarısız',
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <Business sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            Franchise & Zincir Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddDialog}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Yeni Marka Ekle
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <Card elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Marka Listesi</Typography>
            <IconButton onClick={fetchFranchises} color="primary">
              <Refresh />
            </IconButton>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f1f5f9' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Logo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Marka Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell><Skeleton variant="circular" width={40} height={40} /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={80} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))
                ) : franchises.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>Kayıtlı zincir marka bulunamadı.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  franchises.map((franchise) => (
                    <TableRow key={franchise.id} hover>
                      <TableCell>
                        <Avatar src={franchise.logoURL} sx={{ bgcolor: 'orange' }}>
                          {franchise.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{franchise.name}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpenEditDialog(franchise)} color="primary" size="small" sx={{ mr: 1 }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(franchise.id)} color="error" size="small">
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
          {selectedFranchise ? 'Markayı Düzenle' : 'Yeni Marka Ekle'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Marka Adı *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              variant="outlined"
            />
            
            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: '#D2691E',
                  color: '#8B4513',
                  '&:hover': { borderColor: '#8B4513', bgcolor: 'rgba(210, 105, 30, 0.04)' }
                }}
              >
                Logo Seç (Kare/Yuvarlak)
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleLogoUpload}
                />
              </Button>
              {logoPreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Avatar
                    src={logoPreview}
                    alt="Logo Önizleme"
                    sx={{ width: 120, height: 120, margin: '0 auto', border: '2px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  />
                </Box>
              )}
            </Box>

            {!selectedFranchise && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Ana Yönetici Hesabı (Genel Merkez)
                </Typography>
                <TextField
                  label="Yönetici Adı Soyadı"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="Ahmet Yılmaz"
                />
                <TextField
                  label="Yönetici E-posta"
                  value={ownerEmail}
                  type="email"
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="ornek@espressolab.com"
                />
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                  Yönetici bilgileri girilirse, bu zincir marka için tüm şubeleri kontrol edebilen bir "Genel Merkez" hesabı oluşturulacak ve giriş bilgileri e-posta ile gönderilecektir.
                </Alert>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }} disabled={saving}>İptal</Button>
          <Button onClick={handleSave} variant="contained" sx={{ borderRadius: 2, bgcolor: '#8B4513', '&:hover': { bgcolor: '#70360f' } }} disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crop Dialog */}
      <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>Logo Düzenle</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ position: 'relative', width: '100%', height: 400, backgroundColor: '#000', borderRadius: 2, overflow: 'hidden' }}>
            {imageToCrop && (
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <Box sx={{ mt: 3, px: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, value) => setZoom(value as number)}
              sx={{ color: '#8B4513' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setCropDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>İptal</Button>
          <Button onClick={createCroppedImage} variant="contained" sx={{ borderRadius: 2, bgcolor: '#8B4513', '&:hover': { bgcolor: '#70360f' } }}>
            Tamam
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FranchisesPage;
